import type { OrderCardMessage } from './Messages/OrderCard'
import type { ChatBackend } from './CommunicationService'
import type {
  MultipleActionsMessage,
  MultipleActionsMessageActionPayload,
} from './Messages/MultipleActionsMessage'
import type { OrderListMessageDEPRECATED } from './Messages/OrderListMessageDEPRECATED'
import type { OrderListMessage } from './Messages/OrderList'
import type { ProductListMessage } from './Messages/ProductListMessage'
import type { TextMessage, TextMessagePayload } from './Messages/TextMessage'
import type { ClosePromptMessage } from './Messages/ClosePromptMessage'
import type { AvailableCampaigns } from 'entities/Config'
import type { ProductCardMessage } from './Messages/ProductCardMessage'

/**
 *  Represents, who sent this message.
 *  `assistant` - backend generated message
 *  `user` - obviously
 *  `userlike` - sent by Userlike
 */
export enum Sender {
  Assistant = 'assistant',
  User = 'user',
  Userlike = 'userlike',
  ProductCard = 'product-card',
}

export enum AnswerTypes {
  Text = 'text',
  MultipleActions = 'multiple-actions',
  SelectList = 'select-list',
  OrderListDEPRECATED = 'order-list-deprecated',
  OrderList = 'order-list',
  OrderCard = 'order-card',
  UtilityMessage = 'utility-message',
  Input = 'input',
  Login = 'login',
  Inner = 'inner',
  File = 'file',
  Image = 'image',
  ClosePrompt = 'close-prompt',
  RichText = 'rich-text',
  ProductCard = 'product-card',
  ResetPassword = 'reset-password',
}

export type CustomHandlers = 'close_chat' | 'contact_form'

export interface BaseParameters {
  loginRequired?: boolean
  loginSuccess?: string
  loginFailure?: string
  loginCancel?: string
  initUserlike?: boolean
  initPusher?: boolean
  customHandler?: CustomHandlers
  href?: string
  endBackendB?: boolean
  closeWidget?: boolean
  scrollPoint?: boolean
  autoScrollPoint?: boolean
  switchCampaign?: string
  switchBackend?: ChatBackend
  triggerIntent?: string
  rasaHandoff?: boolean
  orderId?: string
}

interface TrackingParameters {
  action: string
  label: string
  category: string
}
export interface BasePayload<
  ParametersType extends BaseParameters = BaseParameters
> {
  parameters?: ParametersType
  inputPlaceholder?: string
  trigger?: string
}
export interface BaseMessage<PayloadType extends BasePayload = BasePayload> {
  type: AnswerTypes
  delayBeforeAppear?: number
  delayBeforeAppend?: number
  payload?: PayloadType
  /**
   * If `protected` = true message doesnt go to Session Storage
   */
  protected?: boolean
  inputPlaceholder?: string
  tracking?: TrackingParameters
  theme?: AvailableCampaigns
  customerNumber?: string | null
}

export interface EventParametersPayload extends BaseParameters {
  content: string
}

/**
 * Type for actions of select list
 */
export interface SelectListMessageActionPayload extends BasePayload {
  id: number
}

export interface UserMessage
  extends BaseMessage<
    | MultipleActionsMessageActionPayload
    | SelectListMessageActionPayload
    | TextMessagePayload
  > {
  content?: string
  productSearch?: boolean
}

export type AnyTypeMessage =
  | TextMessage
  | MultipleActionsMessage
  | ProductListMessage
  | OrderListMessageDEPRECATED
  | OrderListMessage
  | OrderCardMessage
  | ClosePromptMessage
  | ProductCardMessage

/**
 * Message is Entity that we use for render user's and assistant's messages.
 * Can contain content of generic types: `TextMessage` or `MultipleActionsMessage` etc...
 * this content reflects data and metadata of answer
 */
export default interface Message<TypeOfContent = AnyTypeMessage> {
  sender: Sender
  timestamp: Date
  messageObject: TypeOfContent & { intent?: string; isFreeText?: boolean }
  avatar?: string
  name?: string
}

export interface MessageGroup {
  key: string
  sender: Sender
  messages: Array<Message>
  isLast?: boolean
}
